import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
    Box,
    Drawer,
    IconButton,
    TextField,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Menu,
    MenuItem,
    Paper,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Send as SendIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    AutoFixHigh as MagicIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';

const CodeBlock = ({ node, inline, className, children, ...props }) => {
    const [copied, setCopied] = useState(false);
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';

    const handleCopy = () => {
        navigator.clipboard.writeText(String(children).replace(/\n$/, ''));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return !inline ? (
        <Box sx={{ position: 'relative', my: 0.5 }}>
            <Tooltip title={copied ? "Copié !" : "Copier le code"}>
                <IconButton
                    onClick={handleCopy}
                    size="small"
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: '#ffffff',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <CopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <SyntaxHighlighter
                style={vscDarkPlus}
                language={language}
                PreTag="div"
                {...props}
                customStyle={{
                    margin: 0,
                    padding: '0.75rem',
                    borderRadius: '4px',
                    backgroundColor: '#1e1e1e'
                }}
            >
                {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
        </Box>
    ) : (
        <code className={className} {...props}>
            {children}
        </code>
    );
};

const MarkdownRenderer = ({ content }) => {
    return (
        <ReactMarkdown
            components={{
                code: CodeBlock,
                p: ({ children }) => {
                    // Vérifier si le paragraphe contient un bloc de code
                    const hasCodeBlock = React.Children.toArray(children).some(
                        child => React.isValidElement(child) && child.type === CodeBlock
                    );
                    
                    if (hasCodeBlock) {
                        return <>{children}</>;
                    }
                    return <p>{children}</p>;
                }
            }}
        >
            {content}
        </ReactMarkdown>
    );
};

const Chat = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [currentStreamResponse, setCurrentStreamResponse] = useState('');
    const [isImageGenEnabled, setIsImageGenEnabled] = useState(false);
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        fetchConversations();
    }, [navigate]);

    useEffect(() => {
        if (selectedConversation) {
            fetchMessages(selectedConversation.id);
        }
    }, [selectedConversation]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, currentStreamResponse]);

    const fetchConversations = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/conversations`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setConversations(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des conversations:', error);
        }
    };

    const fetchMessages = async (conversationId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/conversations/${conversationId}/messages`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setMessages(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des messages:', error);
        }
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
        }
    };

    const handleGenerateImage = async (prompt) => {
        try {
            setIsGeneratingImage(true);
            
            // Si aucune conversation n'est sélectionnée, en créer une nouvelle
            if (!selectedConversation) {
                const newConversationResponse = await axios.post(
                    `${API_BASE_URL}/api/conversations`,
                    { title: `Image: ${prompt.substring(0, 30)}...` },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                setSelectedConversation(newConversationResponse.data);
                await fetchConversations();
                
                // Utiliser l'ID de la nouvelle conversation
                const response = await axios.post(
                    `${API_BASE_URL}/api/generate-image`,
                    { 
                        prompt,
                        conversationId: newConversationResponse.data.id 
                    },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );

                const imageData = response.data.data[0];
                
                // Ajouter l'image générée aux messages
                const newMessage = {
                    role: 'assistant',
                    content: 'Voici l\'image générée à partir de votre prompt :',
                    image_url: imageData.imageURL
                };
                
                setMessages(prev => [...prev, newMessage]);
            } else {
                // Si une conversation existe déjà, l'utiliser
                const response = await axios.post(
                    `${API_BASE_URL}/api/generate-image`,
                    { 
                        prompt,
                        conversationId: selectedConversation.id 
                    },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );

                const imageData = response.data.data[0];
                
                // Ajouter l'image générée aux messages
                const newMessage = {
                    role: 'assistant',
                    content: 'Voici l\'image générée à partir de votre prompt :',
                    image_url: imageData.imageURL
                };
                
                setMessages(prev => [...prev, newMessage]);
            }
            
            // Réinitialiser le message et désactiver le mode génération d'image
            setNewMessage('');
            setIsImageGenEnabled(false);
        } catch (error) {
            console.error('Erreur lors de la génération de l\'image:', error);
        } finally {
            setIsGeneratingImage(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() && !selectedImage) return;

        try {
            let conversationId = selectedConversation?.id;

            // Si la génération d'image est activée, créer une nouvelle conversation si nécessaire
            if (isImageGenEnabled) {
                if (!conversationId) {
                    const newConversationResponse = await axios.post(
                        `${API_BASE_URL}/api/conversations`,
                        { title: `Image: ${newMessage.substring(0, 30)}...` },
                        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                    );
                    conversationId = newConversationResponse.data.id;
                    setSelectedConversation(newConversationResponse.data);
                }
                await handleGenerateImage(newMessage);
                setNewMessage('');
                return;
            }

            if (!conversationId) {
                const newConversationResponse = await axios.post(
                    `${API_BASE_URL}/api/conversations`,
                    { title: newMessage.substring(0, 30) + '...' },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                conversationId = newConversationResponse.data.id;
                setSelectedConversation(newConversationResponse.data);
            }

            // Ajouter le message de l'utilisateur immédiatement
            const userMessage = { 
                role: 'user', 
                content: newMessage,
                image: selectedImage ? URL.createObjectURL(selectedImage) : null 
            };
            setMessages(prev => [...prev, userMessage]);
            setNewMessage('');
            setSelectedImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Préparer les données pour l'envoi
            let requestBody;
            let headers = {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Accept': 'text/event-stream'
            };

            if (selectedImage) {
                // Si une image est sélectionnée, utiliser FormData
                const formData = new FormData();
                formData.append('content', newMessage);
                formData.append('image', selectedImage);
                requestBody = formData;
                // Ne pas définir Content-Type pour FormData, le navigateur le fera automatiquement
            } else {
                // Si pas d'image, utiliser JSON comme avant
                requestBody = JSON.stringify({ content: newMessage });
                headers['Content-Type'] = 'application/json';
            }

            // Envoyer le message et recevoir la réponse en streaming
            const response = await fetch(
                `${API_BASE_URL}/api/conversations/${conversationId}/messages`,
                {
                    method: 'POST',
                    headers: headers,
                    body: requestBody
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
            }

            // Gérer la réponse en streaming comme avant
            let streamedResponse = '';
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');
                
                for (const line of lines) {
                    if (line.trim() === '') continue;
                    if (line.startsWith('data:')) {
                        try {
                            const data = JSON.parse(line.substring(5));
                            if (data.done) continue;
                            if (data.output && data.output.text) {
                                streamedResponse += data.output.text;
                                setCurrentStreamResponse(streamedResponse);
                            }
                        } catch (error) {
                            console.error('Erreur lors du parsing des données:', error);
                        }
                    }
                }
            }

            setMessages(prev => [...prev, { role: 'assistant', content: streamedResponse }]);
            setCurrentStreamResponse('');
        } catch (error) {
            console.error('Erreur lors de l\'envoi du message:', error);
        }
    };

    const handleEditConversation = async () => {
        try {
            await axios.put(
                `${API_BASE_URL}/api/conversations/${selectedConversation.id}`,
                { title: editTitle },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            setEditDialogOpen(false);
            fetchConversations();
        } catch (error) {
            console.error('Erreur lors de la modification de la conversation:', error);
        }
    };

    const handleDeleteConversation = async () => {
        try {
            await axios.delete(
                `${API_BASE_URL}/api/conversations/${selectedConversation.id}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            setSelectedConversation(null);
            setMessages([]);
            fetchConversations();
        } catch (error) {
            console.error('Erreur lors de la suppression de la conversation:', error);
        }
    };

    const renderMessage = (message) => {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                mb: message.role === 'user' ? 0.5 : 1
            }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: message.role === 'user' ? 1 : 1.5,
                        maxWidth: '70%',
                        backgroundColor: message.role === 'user' ? '#1976d2' : '#424242',
                        color: 'white',
                        borderRadius: 2
                    }}
                >
                    {message.image && (
                        <Box sx={{ mb: message.role === 'user' ? 0.5 : 1 }}>
                            <img 
                                src={message.image} 
                                alt="Générée par l'IA" 
                                style={{ maxWidth: '100%', borderRadius: '8px' }} 
                            />
                        </Box>
                    )}
                    {message.image_url && (
                        <Box sx={{ mb: message.role === 'user' ? 0.5 : 1 }}>
                            <img 
                                src={message.image_url} 
                                alt="Générée par l'IA" 
                                style={{ maxWidth: '100%', borderRadius: '8px' }} 
                            />
                        </Box>
                    )}
                    <MarkdownRenderer content={message.content} />
                </Paper>
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#0a0a0a' }}>
            <Drawer
                variant="permanent"
                sx={{
                    width: 280,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: 280,
                        boxSizing: 'border-box',
                        bgcolor: '#111111',
                        borderRight: '1px solid #2a2a2a',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                }}
            >
                <Box sx={{ p: 2, borderBottom: '1px solid #2a2a2a' }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate('/profile')}
                        sx={{
                            color: '#3a86ff',
                            borderColor: '#3a86ff',
                            mb: 1,
                            '&:hover': {
                                borderColor: '#2b76ef',
                                bgcolor: 'rgba(58, 134, 255, 0.1)',
                            },
                        }}
                    >
                        Mon Profil
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                            setSelectedConversation(null);
                            setMessages([]);
                            setNewMessage('');
                            setSelectedImage(null);
                            if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                            }
                        }}
                        sx={{
                            bgcolor: '#3a86ff',
                            color: '#ffffff',
                            '&:hover': {
                                bgcolor: '#2b76ef',
                            },
                        }}
                    >
                        Nouveau Chat
                    </Button>
                </Box>

                <Box sx={{ 
                    p: 2, 
                    flexGrow: 1,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }}>
                    {conversations.map((conversation) => (
                        <Paper
                            key={conversation.id}
                            onClick={() => setSelectedConversation(conversation)}
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                bgcolor: selectedConversation?.id === conversation.id ? '#2a2a2a' : 'transparent',
                                borderRadius: 1,
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                border: 'none',
                                '&:hover': {
                                    bgcolor: '#2a2a2a',
                                },
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: '1px',
                                    bgcolor: '#2a2a2a'
                                },
                                '&:last-child::after': {
                                    display: 'none'
                                }
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#ffffff',
                                    fontSize: '0.9rem',
                                    fontWeight: selectedConversation?.id === conversation.id ? 600 : 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1
                                }}
                            >
                                {conversation.title}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            </Drawer>

            <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                {selectedConversation && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ flexGrow: 1, color: '#ffffff' }}>
                            {selectedConversation.title}
                        </Typography>
                        <IconButton
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                            sx={{ color: '#ffffff' }}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Box>
                )}

                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        overflowY: 'auto',
                        bgcolor: '#121212'
                    }}
                >
                    {messages.map((message, index) => (
                        <Box key={index}>
                            {renderMessage(message)}
                        </Box>
                    ))}
                    {currentStreamResponse && (
                        <Box>
                            {renderMessage({ role: 'assistant', content: currentStreamResponse })}
                        </Box>
                    )}
                    <div ref={messagesEndRef} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageSelect}
                        ref={fileInputRef}
                    />
                    <IconButton
                        onClick={() => fileInputRef.current.click()}
                        disabled={isImageGenEnabled}
                        sx={{
                            color: selectedImage ? '#3a86ff' : '#ffffff',
                            bgcolor: '#1a1a1a',
                            border: '1px solid #2a2a2a',
                            '&:hover': {
                                bgcolor: '#2a2a2a',
                            },
                            '&.Mui-disabled': {
                                color: '#666666',
                            }
                        }}
                    >
                        <ImageIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => setIsImageGenEnabled(!isImageGenEnabled)}
                        disabled={selectedImage !== null}
                        sx={{
                            color: isImageGenEnabled ? '#3a86ff' : '#ffffff',
                            bgcolor: '#1a1a1a',
                            border: '1px solid #2a2a2a',
                            '&:hover': {
                                bgcolor: '#2a2a2a',
                            },
                            '&.Mui-disabled': {
                                color: '#666666',
                            }
                        }}
                    >
                        <MagicIcon />
                    </IconButton>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={
                            isImageGenEnabled 
                                ? "Décrivez l'image que vous souhaitez générer..." 
                                : selectedImage 
                                    ? "Image sélectionnée. Ajoutez un message..." 
                                    : "Écrivez votre message..."
                        }
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                bgcolor: '#1a1a1a',
                                '& fieldset': {
                                    borderColor: '#2a2a2a',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#3a86ff',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3a86ff',
                                },
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSendMessage}
                        disabled={isGeneratingImage}
                        sx={{
                            bgcolor: '#3a86ff',
                            '&:hover': {
                                bgcolor: '#2b76ef',
                            },
                            minWidth: 56,
                        }}
                    >
                        {isGeneratingImage ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            <SendIcon />
                        )}
                    </Button>
                </Box>
            </Box>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                PaperProps={{
                    sx: {
                        bgcolor: '#1a1a1a',
                        color: '#ffffff',
                        border: '1px solid #2a2a2a'
                    }
                }}
            >
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        setEditTitle(selectedConversation.title);
                        setEditDialogOpen(true);
                    }}
                    sx={{ '&:hover': { bgcolor: '#2a2a2a' } }}
                >
                    <EditIcon sx={{ mr: 1 }} /> Modifier
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        handleDeleteConversation();
                    }}
                    sx={{ '&:hover': { bgcolor: '#2a2a2a' } }}
                >
                    <DeleteIcon sx={{ mr: 1 }} /> Supprimer
                </MenuItem>
            </Menu>

            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                PaperProps={{
                    sx: {
                        bgcolor: '#1a1a1a',
                        color: '#ffffff',
                        border: '1px solid #2a2a2a'
                    }
                }}
            >
                <DialogTitle>Modifier la conversation</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Titre"
                        type="text"
                        fullWidth
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': {
                                    borderColor: '#2a2a2a',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#3a86ff',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3a86ff',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#888888',
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#888888' }}>
                        Annuler
                    </Button>
                    <Button onClick={handleEditConversation} sx={{ color: '#3a86ff' }}>
                        Enregistrer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Chat; 